import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import Moment from 'react-moment';

import {
  fetchMatchDetail,
  checkMatchVideoAccess,
  fetchPlayerOverview,
  fetchPlayerParameters,
  fetchPlayerMatchStats,
  fetchMarkersBulk,
} from './api';
import { useMappedPlayerId } from './hooks';
import {
  collectMarkerIds,
  buildEventsFromMarkers,
  formatSeconds,
  parseEventKeys,
} from './matchEvents';
import { getMediaUrl } from '../../helpers';
import PlayerLoader from '../../components/PlayerLoader';
import { useLexicon } from '../../hooks/useLexicon';

const CLIP_BEFORE = 10;
const CLIP_AFTER = 10;

const PlayerMatchVideo = () => {
  const l = useLexicon();
  const { id: matchId } = useParams();
  const [searchParams] = useSearchParams();
  const mappedPlayerId = useMappedPlayerId();
  const videoRef = useRef(null);

  const [accessBlocked, setAccessBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [match, setMatch] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [matchRow, setMatchRow] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(null);
  const [activeClipIndex, setActiveClipIndex] = useState(0);

  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const accessRes = await checkMatchVideoAccess(matchId);
        if (cancelled) return;
        if (!accessRes.data?.has_access) {
          setAccessBlocked(true);
          setLoading(false);
          return;
        }

        const [detailRes, overviewRes, paramsRes] = await Promise.all([
          fetchMatchDetail(matchId),
          fetchPlayerOverview(mappedPlayerId),
          fetchPlayerParameters(),
        ]);
        if (cancelled) return;

        const detail = detailRes.data;
        const homeTeam = detail.home_squad?.team;
        const awayTeam = detail.away_squad?.team;
        const playableVideo = (detail.videos || []).find((v) => Boolean(v?.link));
        // team.logo is the absolute URL (api-v0/...), team.logo_only is the
        // bare path. getMediaUrl expects a URL or absolute path under /media —
        // pass the full logo so the rewrite works correctly.
        setMatch({
          ...detail,
          home_team: homeTeam?.name,
          away_team: awayTeam?.name,
          home_team_logo: homeTeam?.logo,
          away_team_logo: awayTeam?.logo,
        });
        setVideoUrl(playableVideo?.link || null);

        const paramsData = paramsRes.data?.results ?? paramsRes.data ?? [];

        const overview = overviewRes.data;
        const resolvedSeasonId = detail?.season?.year_season?.id
          ?? overview?.selected_season?.id
          ?? overview?.available_seasons?.[0]?.id;
        const paramIds = (Array.isArray(paramsData) ? paramsData : []).map((p) => p.id);

        if (resolvedSeasonId && paramIds.length > 0) {
          const statsRes = await fetchPlayerMatchStats(mappedPlayerId, resolvedSeasonId, paramIds);
          if (cancelled) return;
          const rows = Array.isArray(statsRes.data)
            ? statsRes.data
            : Array.isArray(statsRes.data?.results) ? statsRes.data.results : [];
          const row = rows.find((m) => String(m?.id) === String(matchId));
          setMatchRow(row || null);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [matchId, mappedPlayerId]);

  useEffect(() => {
    if (!matchRow || !mappedPlayerId) {
      setEvents([]);
      return undefined;
    }
    const markerIds = collectMarkerIds(matchRow);
    if (markerIds.length === 0) {
      setEvents([]);
      return undefined;
    }

    let cancelled = false;
    setEventsLoading(true);

    fetchMarkersBulk(markerIds)
      .then((res) => {
        if (cancelled) return;
        const markers = res.data?.events ?? [];
        setEvents(buildEventsFromMarkers(markers));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });

    return () => { cancelled = true; };
  }, [matchRow, mappedPlayerId, matchId]);

  useEffect(() => {
    if (events.length === 0 || selectedKeys !== null) return;
    const fromQuery = parseEventKeys(searchParams.get('events'));
    const valid = new Set(events.map((e) => e.key));
    const preselect = fromQuery.size > 0
      ? new Set(Array.from(fromQuery).filter((k) => valid.has(k)))
      : new Set(valid);
    setSelectedKeys(preselect);
    setActiveClipIndex(0);
  }, [events, searchParams, selectedKeys]);

  const clips = useMemo(() => {
    if (!selectedKeys) return [];
    return events
      .filter((e) => selectedKeys.has(e.key))
      .map((e) => ({
        ...e,
        start: Math.max(0, e.second - CLIP_BEFORE),
        end: e.second + CLIP_AFTER,
      }));
  }, [events, selectedKeys]);

  const activeClip = clips[activeClipIndex] ?? null;
  const activeClipKey = activeClip?.key ?? null;

  const toggleEvent = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev || []);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const jumpToEvent = (key) => {
    setSelectedKeys((prev) => {
      if (prev && prev.has(key)) return prev;
      const next = new Set(prev || []);
      next.add(key);
      return next;
    });
    const idx = clips.findIndex((c) => c.key === key);
    if (idx >= 0) setActiveClipIndex(idx);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return;
    try {
      video.currentTime = activeClip.start;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } catch (e) {
      // Seeking before metadata loaded; loadedmetadata listener retries below.
    }
  }, [activeClipKey]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return undefined;
    const onTimeUpdate = () => {
      if (!activeClip) return;
      if (video.currentTime >= activeClip.end) {
        if (activeClipIndex < clips.length - 1) {
          setActiveClipIndex(activeClipIndex + 1);
        } else {
          video.pause();
        }
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [activeClip, activeClipIndex, clips.length]);

  if (!mappedPlayerId) {
    return <Navigate to="/player/not-mapped" replace />;
  }

  return (
    <Wrap>
      <TopBar>
        <BackLink to={`/player/matches/${matchId}`}>← {l(451)}</BackLink>
      </TopBar>

      {loading && <PlayerLoader label={l(1839)} />}
      {error && <ErrorBox>{error.message || l(1840)}</ErrorBox>}
      {accessBlocked && (
        <ErrorBox>{l(1841)}</ErrorBox>
      )}

      {match && !loading && (
        <Header>
          <Teams>
            <TeamSide>
              {match.home_team_logo && (
                <TeamLogo src={getMediaUrl(match.home_team_logo)} alt="" />
              )}
              <TeamName>{match.home_team}</TeamName>
            </TeamSide>
            <ScoreBlock>
              <MatchDate>
                <Moment format="DD.MM.YYYY">{match.date}</Moment>
              </MatchDate>
              <Score>
                {match.score || `${match.home_team_score}:${match.away_team_score}`}
              </Score>
            </ScoreBlock>
            <TeamSide>
              {match.away_team_logo && (
                <TeamLogo src={getMediaUrl(match.away_team_logo)} alt="" />
              )}
              <TeamName>{match.away_team}</TeamName>
            </TeamSide>
          </Teams>
        </Header>
      )}

      {videoUrl && !accessBlocked && (
        <VideoStage>
          <StyledVideo
            ref={videoRef}
            src={videoUrl}
            controls
            playsInline
            preload="metadata"
          />
        </VideoStage>
      )}

      {!loading && !accessBlocked && eventsLoading && events.length === 0 && (
        <PlayerLoader label={l(1831)} />
      )}

      {!loading && !accessBlocked && !eventsLoading && events.length === 0 && (
        <Muted>{l(1842)}</Muted>
      )}

      {events.length > 0 && (
        <Panel>
          <PanelHeader>
            <PanelTitle>{l(1832)} · {selectedKeys?.size ?? 0} / {events.length}</PanelTitle>
            <NavButtons>
              <NavButton
                type="button"
                disabled={activeClipIndex <= 0}
                onClick={() => setActiveClipIndex((i) => Math.max(0, i - 1))}
              >
                ‹ Prev
              </NavButton>
              <PlaylistCounter>
                {clips.length > 0 ? `${activeClipIndex + 1} / ${clips.length}` : '0 / 0'}
              </PlaylistCounter>
              <NavButton
                type="button"
                disabled={clips.length === 0 || activeClipIndex >= clips.length - 1}
                onClick={() => setActiveClipIndex((i) => Math.min(clips.length - 1, i + 1))}
              >
                Next ›
              </NavButton>
            </NavButtons>
          </PanelHeader>

          <EventList>
            {events.map((e) => {
              const checked = selectedKeys?.has(e.key) ?? false;
              const isActive = activeClipKey === e.key;
              return (
                <EventRow key={e.key} $active={isActive} $checked={checked}>
                  <EventCheckbox
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleEvent(e.key)}
                  />
                  <EventMain
                    type="button"
                    onClick={() => jumpToEvent(e.key)}
                  >
                    <EventName>{e.paramName}</EventName>
                    <EventTime>{formatSeconds(e.second)}</EventTime>
                  </EventMain>
                </EventRow>
              );
            })}
          </EventList>

          {selectedKeys && selectedKeys.size === 0 && (
            <Muted>{l(1843)}</Muted>
          )}
        </Panel>
      )}
    </Wrap>
  );
};

export default PlayerMatchVideo;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.blueish2};
  text-decoration: none;
  font-size: 14px;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const Header = styled.div`
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 12px;
`;

const Teams = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const TeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const TeamLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const TeamName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  text-align: center;
`;

const ScoreBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const MatchDate = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
`;

const Score = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 18px;
`;

const VideoStage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PanelTitle = styled.h3`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const NavButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NavButton = styled.button`
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => props.theme.white};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PlaylistCounter = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  min-width: 44px;
  text-align: center;
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 10px;
  overflow: hidden;
  max-height: 50vh;
  overflow-y: auto;
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${(props) => (props.$active
    ? props.theme.green
    : props.$checked ? props.theme.row : 'transparent')};
  color: ${(props) => (props.$active ? props.theme.black : props.theme.white)};
  border-top: 1px solid ${(props) => props.theme.borderColor};

  &:first-child { border-top: 0; }
`;

const EventCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${(props) => props.theme.green};
  cursor: pointer;
  flex-shrink: 0;
`;

const EventMain = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  padding: 2px 0;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  text-align: left;
`;

const EventName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;

const EventTime = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  opacity: 0.8;
  flex-shrink: 0;
`;
